import { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

/**
 * useGroups
 * Reads the user's Cognito groups from the JWT ID token.
 * Returns { groups: string[], loading: boolean }
 *
 * Groups are cached in a module-level variable after the first fetch
 * so subsequent calls (navigation, remounts) are instant.
 *
 * Cache is invalidated on sign out via clearGroupsCache().
 *
 * Note: if a user's groups change in Cognito, they must sign out and back
 * in for the new groups to appear here (Cognito JWT reflects groups at
 * login time).
 */

// Module-level cache — survives component remounts and navigation
let _cachedGroups = null;
let _fetchPromise = null;

export function clearGroupsCache() {
  _cachedGroups = null;
  _fetchPromise = null;
}

function fetchGroups() {
  // Return existing promise if already in flight — deduplicates concurrent calls
  if (_fetchPromise) return _fetchPromise;

  _fetchPromise = fetchAuthSession()
    .then(session => {
      const payload = session.tokens?.idToken?.payload || {};
      const raw = payload['cognito:groups'] || [];
      _cachedGroups = Array.isArray(raw) ? raw : [raw];
      return _cachedGroups;
    })
    .catch(() => {
      _cachedGroups = [];
      _fetchPromise = null; // allow retry on error
      return [];
    });

  return _fetchPromise;
}

export function useGroups() {
  const [groups,  setGroups]  = useState(() => _cachedGroups || []);
  const [loading, setLoading] = useState(() => _cachedGroups === null);

  useEffect(() => {
    // If already cached, nothing to do
    if (_cachedGroups !== null) {
      setGroups(_cachedGroups);
      setLoading(false);
      return;
    }
    fetchGroups().then(g => {
      setGroups(g);
      setLoading(false);
    });
  }, []);

  return { groups, loading };
}

/**
 * hasAccess
 * Returns true if the user's groups include at least one of the allowed groups.
 */
export function hasAccess(userGroups, allowedGroups) {
  return allowedGroups.some(g => userGroups.includes(g));
}